import * as React from "react"
import { useTranslation } from "react-i18next"
import { motion } from "framer-motion"
import DashboardLayout from "@/components/layout/DashboardLayout"
import { AppleButton } from "@/components/ui/apple-button"
import { Tag, Zap, Shield, Smartphone, ShoppingCart, Sticker, QrCode, Scan } from "lucide-react"

export default function TivoTags() {
  const { t } = useTranslation()

  return (
    <DashboardLayout>
      <div className="max-w-7xl -ml-64 mx-auto p-6">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-blue-50/50 to-white rounded-3xl p-8 mb-8">
          <div className="flex flex-col md:flex-row items-center gap-8">
            {/* Left side - Product Image */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="w-full md:w-1/2"
          >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="/images/tivoTaggStore.jpg"
                  alt={t('nfcTag.title')}
                  className="w-full h-auto object-cover transform hover:scale-105 transition-transform duration-500"
                />
                {/* Floating specs */}
                <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-lg p-3 shadow-lg">
                  <p className="text-sm font-semibold">{t('nfcTag.specs.frequency.value')}</p>
                  <p className="text-xs text-gray-600">{t('nfcTag.specs.frequency.label')}</p>
                </div>
              </div>
            </motion.div>

            {/* Right side - Content */}
            <div className="w-full md:w-1/2 space-y-6">
          <motion.div
                initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
                <h1 className="text-4xl font-bold text-gray-900 mb-4">{t('nfcTag.title')}</h1>
                <p className="text-2xl md:text-3xl text-gray-800 mb-8">
                  {t('nfcTag.subtitle')}
                </p>
                <a
                  href="https://buy.stripe.com/fZe4gV9IP4C89z228h"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-8 py-4 rounded-full text-lg font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-colors shadow-lg hover:shadow-xl"
                >
                  {t('nfcTag.buyButton')}
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </a>
          </motion.div>
        </div>
            </div>
          </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow"
          >
            <Tag className="h-6 w-6 text-blue-600 mb-4" />
            <h3 className="font-semibold mb-2">{t('nfcTag.features.weatherResistant.title')}</h3>
            <p className="text-gray-600">{t('nfcTag.features.weatherResistant.value')}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow"
          >
            <Zap className="h-6 w-6 text-blue-600 mb-4" />
            <h3 className="font-semibold mb-2">{t('nfcTag.features.durability.title')}</h3>
            <p className="text-gray-600">{t('nfcTag.features.durability.value')}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow"
          >
            <Shield className="h-6 w-6 text-blue-600 mb-4" />
            <h3 className="font-semibold mb-2">{t('nfcTag.features.metalCompatible.title')}</h3>
            <p className="text-gray-600">{t('nfcTag.features.metalCompatible.value')}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow"
          >
            <Smartphone className="h-6 w-6 text-blue-600 mb-4" />
            <h3 className="font-semibold mb-2">{t('nfcTag.features.easyApplication.title')}</h3>
            <p className="text-gray-600">{t('nfcTag.features.easyApplication.value')}</p>
          </motion.div>
        </div>

        {/* Steps Section */}
        <div className="mt-12 mb-12">
          <h2 className="text-2xl font-bold text-center mb-8">{t('steps.title')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-2xl p-6 shadow-lg relative"
            >
              <div className="absolute -top-4 -left-4 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold">
                1
              </div>
              <ShoppingCart className="h-8 w-8 text-blue-600 mb-4" />
              <h3 className="font-semibold mb-2">{t('steps.buyTags.title')}</h3>
              <p className="text-gray-600 text-sm mb-4">{t('steps.buyTags.description')}</p>
              <a
                href="https://buy.stripe.com/fZe4gV9IP4C89z228h"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-colors"
              >
                {t('nfcTag.buyButton')}
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-2xl p-6 shadow-lg relative"
            >
              <div className="absolute -top-4 -left-4 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold">
                2
              </div>
              <Sticker className="h-8 w-8 text-blue-600 mb-4" />
              <h3 className="font-semibold mb-2">{t('steps.applyTag.title')}</h3>
              <p className="text-gray-600 text-sm">{t('steps.applyTag.description')}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-white rounded-2xl p-6 shadow-lg relative"
            >
              <div className="absolute -top-4 -left-4 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold">
                3
              </div>
              <QrCode className="h-8 w-8 text-blue-600 mb-4" />
              <h3 className="font-semibold mb-2">{t('steps.scanTrack.title')}</h3>
              <p className="text-gray-600 text-sm">{t('steps.scanTrack.description')}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-white rounded-2xl p-6 shadow-lg relative border-2 border-blue-100"
            >
              <div className="absolute -top-4 -left-4 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold">
                +
              </div>
              <Scan className="h-8 w-8 text-blue-600 mb-4" />
              <h3 className="font-semibold mb-2">{t('steps.existingTags.title')}</h3>
              <p className="text-gray-600 text-sm">{t('steps.existingTags.description')}</p>
            </motion.div>
          </div>
        </div>

        {/* Protocol and Compatibility Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="bg-white rounded-2xl p-8 shadow-lg"
          >
            <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Shield className="h-5 w-5 text-blue-600" />
              {t('nfcTag.protocol.title')}
            </h3>
            <p className="text-gray-600">{t('nfcTag.protocol.description')}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="bg-white rounded-2xl p-8 shadow-lg"
          >
            <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Smartphone className="h-5 w-5 text-blue-600" />
              {t('nfcTag.compatibility.title')}
            </h3>
            <p className="text-gray-600">{t('nfcTag.compatibility.description')}</p>
          </motion.div>
        </div>
      </div>
    </DashboardLayout>
  )
} 
